import type { Asset } from "@/lib/types"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { useTemplates } from "@/hooks/useTemplates"
import { useAssets } from "@/hooks/useAssets"
import { Loader2, Trash2, AlertTriangle, Plus } from "lucide-react"
import { useState } from "react"

interface TemplateSelectorProps {
  onApplyTemplate: (assets: Omit<Asset, "id">[], templateId?: string) => void
  artWidth: number
  artHeight: number
}

export function TemplateSelector({ onApplyTemplate, artWidth, artHeight }: TemplateSelectorProps) {
  const { templates, loading, getTemplateWithLayers, deleteTemplate, deleteAllTemplates, updateTemplate, createTemplate } = useTemplates()
  const { assets: libraryAssets } = useAssets()
  const [isDeleteAllOpen, setIsDeleteAllOpen] = useState(false)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState("")
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState("")
  const [newTemplateWidth, setNewTemplateWidth] = useState("1024")
  const [newTemplateHeight, setNewTemplateHeight] = useState("1024")

  const handleTemplateChange = async (templateId: string) => {
    const template = await getTemplateWithLayers(templateId)
    if (template && template.layers) {
      // Converte layers do backend para formato Asset
      const assets = template.layers
        .filter((layer) => layer.kind === 'asset')
        .map((layer) => {
          // Procura o asset correspondente na biblioteca pelo asset_id
          const assetData = libraryAssets.find((a) => a.id === layer.asset_id)
          
          return {
            name: assetData?.name || `Asset ${layer.asset_id}`,
            imageUrl: assetData?.imageUrl || '', // Use imageUrl do asset encontrado
            x: layer.x,
            y: layer.y,
            z: layer.z_index,
            w: layer.w || assetData?.w || 100,
            h: layer.h || assetData?.h || 100,
            rotation: layer.rotation || 0,
            backendAssetId: layer.asset_id, // Preservar ID do backend
          }
        })
        // Filtrar assets com imageUrl válida
        .filter((asset) => asset.imageUrl)
      
      onApplyTemplate(assets, templateId)
    }
  }

  const handleRenameStart = (templateId: string, currentName: string) => {
    setRenamingId(templateId)
    setRenameValue(currentName)
  }

  const handleRenameSubmit = async (templateId: string) => {
    if (!renameValue.trim()) return
    await updateTemplate(templateId, renameValue)
    setRenamingId(null)
    setRenameValue("")
  }

  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim()) return
    const width = parseInt(newTemplateWidth) || 1024
    const height = parseInt(newTemplateHeight) || 1024
    await createTemplate(newTemplateName, width, height)
    setIsCreateOpen(false)
    setNewTemplateName("")
  }

  // Ordenar templates: mais novos em cima
  const sortedTemplates = [...templates].sort((a, b) => {
    const dateA = new Date(a.created_at || 0).getTime()
    const dateB = new Date(b.created_at || 0).getTime()
    return dateB - dateA
  })

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Carregando templates...
      </div>
    )
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" title={`Templates (${templates.length})`}>
          <span className="text-lg">📋</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Meus Templates</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {sortedTemplates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum template salvo. Crie um novo no editor!
            </p>
          ) : (
            sortedTemplates.map((template) => (
              <div
                key={template.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent transition-colors"
              >
                {renamingId === template.id ? (
                  <div className="flex-1 flex gap-2 mr-2">
                    <Input
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameSubmit(template.id)
                        } else if (e.key === 'Escape') {
                          setRenamingId(null)
                        }
                      }}
                      autoFocus
                      className="flex-1"
                    />
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleRenameSubmit(template.id)}
                      className="px-2"
                    >
                      ✓
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRenamingId(null)}
                      className="px-2"
                    >
                      ✕
                    </Button>
                  </div>
                ) : (
                  <>
                    <div
                      onClick={() => handleTemplateChange(template.id)}
                      className="flex-1 cursor-pointer"
                    >
                      <p
                        className="font-medium text-sm hover:underline cursor-text"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRenameStart(template.id, template.name)
                        }}
                      >
                        {template.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {template.canvas_width}×{template.canvas_height}px
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTemplateChange(template.id)}
                      className="h-8 w-8 p-0 hover:bg-green-500/10 hover:text-green-600"
                      title="Aplicar template"
                    >
                      ✓
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteTemplate(template.id)}
                      className="h-8 w-8 p-0 hover:bg-red-500/10 hover:text-red-600"
                      title="Deletar template"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        <div className="pt-3 border-t space-y-2">
          <Dialog open={isCreateOpen} onOpenChange={(open) => {
            setIsCreateOpen(open);
            if (open) {
              setNewTemplateWidth(artWidth?.toString() || '1024');
              setNewTemplateHeight(artHeight?.toString() || '1024');
            }
          }}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Template
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Novo Template</DialogTitle>
              </DialogHeader>
              <div className="grid gap-2 py-4">
                <div className="grid gap-1">
                  <label htmlFor="newTemplateName" className="text-sm font-medium">
                    Nome do Template
                  </label>
                  <Input
                    id="newTemplateName"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    placeholder="Meu Template"
                  />
                </div>
                <div className="grid gap-1">
                  <label htmlFor="newTemplateWidth" className="text-sm font-medium">
                    Largura (px)
                  </label>
                  <Input
                    id="newTemplateWidth"
                    type="number"
                    value={newTemplateWidth}
                    onChange={(e) => setNewTemplateWidth(e.target.value)}
                    placeholder="1024"
                  />
                </div>
                <div className="grid gap-1">
                  <label htmlFor="newTemplateHeight" className="text-sm font-medium">
                    Altura (px)
                  </label>
                  <Input
                    id="newTemplateHeight"
                    type="number"
                    value={newTemplateHeight}
                    onChange={(e) => setNewTemplateHeight(e.target.value)}
                    placeholder="1024"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateTemplate}>
                  Criar Template
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {templates.length > 0 && (
            <Dialog open={isDeleteAllOpen} onOpenChange={setIsDeleteAllOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full"
                >
                  🗑️ Limpar Todos
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    Deletar todos os templates?
                  </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  Isso vai deletar permanentemente {templates.length} template(s). Esta ação não pode ser desfeita.
                </p>
                <div className="flex gap-2 justify-end pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setIsDeleteAllOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      await deleteAllTemplates()
                      setIsDeleteAllOpen(false)
                    }}
                  >
                    Deletar Todos
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}


